"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectInsightAttributesMeta = exports.QueryInsightAttributesMetaService = void 0;
var tslib_1 = require("tslib");
var sdk_model_1 = require("@gooddata/sdk-model");
var queryService_1 = require("../store/_infra/queryService");
var insightsSelectors_1 = require("../store/insights/insightsSelectors");
var effects_1 = require("redux-saga/effects");
var general_1 = require("../events/general");
var catalogSelectors_1 = require("../store/catalog/catalogSelectors");
var ts_invariant_1 = require("ts-invariant");
var uniqBy_1 = tslib_1.__importDefault(require("lodash/uniqBy"));
exports.QueryInsightAttributesMetaService = queryService_1.createCachedQueryService("GDC.DASH/QUERY.INSIGHT.ATTRIBUTE.META", queryService, function (query) {
    var insightOrRef = query.payload.insightOrRef;
    var ref = sdk_model_1.isObjRef(insightOrRef) ? insightOrRef : sdk_model_1.insightRef(insightOrRef);
    return sdk_model_1.serializeObjRef(ref);
});
/**
 * Selector that will return attribute metadata for an insight. The input to the selector is the dashboard query that is used
 * to obtain and cache the data.
 *
 * This selector will return undefined if the query to obtain the data for particular insight was not yet fired or
 * processed. Otherwise will return object containing `status` of the data retrieval; if the `status` is
 * `'success'` then the `result` prop will contain the data.
 *
 * @remarks see {@link QueryInsightAttributesMeta}
 * @internal
 */
exports.selectInsightAttributesMeta = exports.QueryInsightAttributesMetaService.cache.selectQueryResult;
//
// Query implementation
//
function loadDisplayFormsAndAttributes(ctx, displayFormRefs) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var backend, workspace, loadedDisplayForms, loadedAttributes;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    backend = ctx.backend, workspace = ctx.workspace;
                    return [4 /*yield*/, backend
                            .workspace(workspace)
                            .attributes()
                            .getAttributeDisplayForms(displayFormRefs)];
                case 1:
                    loadedDisplayForms = _a.sent();
                    return [4 /*yield*/, backend
                            .workspace(workspace)
                            .attributes()
                            .getAttributes(loadedDisplayForms.map(function (df) { return df.attribute; }))];
                case 2:
                    loadedAttributes = _a.sent();
                    return [2 /*return*/, {
                            loadedDisplayForms: loadedDisplayForms,
                            loadedAttributes: loadedAttributes,
                        }];
            }
        });
    });
}
/**
 * Loads insight attribute and display form metadata for particular insight display form usage.
 *
 * This function will first attempt to find display forms & attributes in the catalog - for a quick in memory hit.
 *
 * However in corner cases some of the display forms and attributes may not be stored in catalog. That is the case when dashboard
 * contains an insight created from date datasets created from custom CSVs. These are not stored in the 'default' catalog
 * that is part of the dashboard component's state. That's when the function falls back to load the metadata from backend.
 */
function createInsightAttributesMeta(ctx, usage, catalogDisplayForms, catalogAttributes) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var allUsedRefs, displayFormsFromCatalog, missingDisplayForms, attributesFromCatalog, _a, loadedDisplayForms, loadedAttributes;
        return tslib_1.__generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    allUsedRefs = tslib_1.__spread(usage.inAttributes, usage.inFilters, usage.inMeasureFilters);
                    displayFormsFromCatalog = [];
                    missingDisplayForms = [];
                    allUsedRefs.forEach(function (ref) {
                        var catalogDisplayForm = catalogDisplayForms.get(ref);
                        if (catalogDisplayForm) {
                            displayFormsFromCatalog.push(catalogDisplayForm);
                        }
                        else {
                            missingDisplayForms.push(ref);
                        }
                    });
                    attributesFromCatalog = displayFormsFromCatalog.map(function (df) {
                        var catalogAttribute = catalogAttributes.get(df.attribute);
                        // if this bombs there must be something seriously wrong because it means a display form is in catalog
                        // but the attribute to which it belongs is not. such a thing can only happen if something is messing
                        // around with the catalog state stored in the component.
                        ts_invariant_1.invariant(catalogAttribute, "dashboard metadata catalog is corrupted");
                        return catalogAttribute.attribute;
                    });
                    return [4 /*yield*/, loadDisplayFormsAndAttributes(ctx, missingDisplayForms)];
                case 1:
                    _a = _b.sent(), loadedDisplayForms = _a.loadedDisplayForms, loadedAttributes = _a.loadedAttributes;
                    return [2 /*return*/, {
                            usage: usage,
                            attributes: uniqBy_1.default(tslib_1.__spread(attributesFromCatalog, loadedAttributes), function (a) { return sdk_model_1.serializeObjRef(a.ref); }),
                            displayForms: uniqBy_1.default(tslib_1.__spread(displayFormsFromCatalog, loadedDisplayForms), function (df) {
                                return sdk_model_1.serializeObjRef(df.ref);
                            }),
                        }];
            }
        });
    });
}
function queryService(ctx, query) {
    var correlationId, insightOrRef, insight, usage, catalogDisplayForms, catalogAttributes;
    return tslib_1.__generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                correlationId = query.correlationId, insightOrRef = query.payload.insightOrRef;
                if (!sdk_model_1.isObjRef(insightOrRef)) return [3 /*break*/, 2];
                return [4 /*yield*/, effects_1.select(insightsSelectors_1.selectInsightByRef(insightOrRef))];
            case 1:
                insight = _a.sent();
                if (!insight) {
                    throw general_1.invalidQueryArguments(ctx, "Insight with ref " + sdk_model_1.objRefToString(insightOrRef) + " does not exist on the dashboard", correlationId);
                }
                return [3 /*break*/, 3];
            case 2:
                insight = insightOrRef;
                _a.label = 3;
            case 3:
                usage = sdk_model_1.insightDisplayFormUsage(insight);
                return [4 /*yield*/, effects_1.select(catalogSelectors_1.selectAllCatalogDisplayFormsMap)];
            case 4:
                catalogDisplayForms = _a.sent();
                return [4 /*yield*/, effects_1.select(catalogSelectors_1.selectAllCatalogAttributesMap)];
            case 5:
                catalogAttributes = _a.sent();
                return [4 /*yield*/, effects_1.call(createInsightAttributesMeta, ctx, usage, catalogDisplayForms, catalogAttributes)];
            case 6: return [2 /*return*/, _a.sent()];
        }
    });
}
//# sourceMappingURL=queryInsightAttributesMeta.js.map